import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit, FaTrash } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import { supabase } from "../../supabaseClient";

const RatesNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [rate, setRate] = useState(null);
  const [ratesData, setRatesData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [locations, setLocations] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [refresh, setRefresh] = useState(0);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      LocationID: "",
      AirlineID: "",
      Rate: "",
    },
  });

  useEffect(() => {
    if (rate) {
      reset({
        LocationID: rate.LocationID,
        AirlineID: rate.AirlineID || "",
        Rate: rate.Rate,
      });
    }
  }, [rate, reset]);

  useEffect(() => {
    const fetchLookups = async () => {
      const { data: locs } = await supabase
        .from("Locations")
        .select("LocationID, Code, Description")
        .order("Code");
      setLocations(locs || []);
      const { data: airs } = await supabase
        .from("Airlines")
        .select("AirlineID, AirlineLetterCode, Description")
        .order("AirlineLetterCode");
      setAirlines(airs || []);
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    const fetchRates = async () => {
      const { data, count, error } = await supabase
        .from("LocationAirlineRates")
        .select(
          `ID, LocationID, AirlineID, Rate,
           Locations:LocationID (Code, Description),
           Airlines:AirlineID (AirlineLetterCode, Description)`,
          { count: "exact" }
        )
        .order("LocationID")
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
      if (!error) {
        setRatesData(data || []);
        setTotalCount(count || 0);
      }
    };
    fetchRates();
  }, [page, refresh]);

  const onNextPage = useCallback(() => setPage((old) => old + 1), []);
  const onPrevPage = useCallback(() => setPage((old) => Math.max(old - 1, 1)), []);

  const onSuccess = useCallback(() => {
    toast.success("Success");
    setIsEdit(false);
    setRate(null);
    setRefresh((r) => r + 1);
  }, []);

  const onError = useCallback((err) => {
    toast.error("Error: " + (err.message || err));
  }, []);

  const onAddNew = () => {
    reset({
      LocationID: locations[0] ? locations[0].LocationID : "",
      AirlineID: "",
      Rate: "",
    });
    setIsEdit(true);
    setRate(null);
  };

  const onCancel = () => {
    setIsEdit(false);
    setRate(null);
  };

  const onSave = useCallback(
    async (data) => {
      const payload = {
        LocationID: parseInt(data.LocationID),
        AirlineID: data.AirlineID ? parseInt(data.AirlineID) : null,
        Rate: parseFloat(data.Rate),
      };

      if (rate) {
        const { error } = await supabase
          .from("LocationAirlineRates")
          .update(payload)
          .eq("ID", rate.ID);
        if (error) onError(error);
        else onSuccess();
      } else {
        const { error } = await supabase
          .from("LocationAirlineRates")
          .insert(payload);
        if (error) {
          if (error.code === "23505") {
            onError({ message: "A rate for this location + airline already exists" });
          } else {
            onError(error);
          }
        } else {
          onSuccess();
        }
      }
    },
    [rate, onError, onSuccess]
  );

  const onDelete = useCallback(
    async (id) => {
      if (!window.confirm("Delete this rate?")) return;
      const { error } = await supabase
        .from("LocationAirlineRates")
        .delete()
        .eq("ID", id);
      if (error) onError(error);
      else {
        toast.success("Rate deleted");
        setRefresh((r) => r + 1);
      }
    },
    [onError]
  );

  return (
    <>
      {isEdit ? (
        <div className="driver-patent-edit-content mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>Airport</div>
            <div className="w-35">
              <Controller
                control={control}
                name="LocationID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                  >
                    <option value="">-- Select --</option>
                    {locations.map((loc) => (
                      <option key={loc.LocationID} value={loc.LocationID}>
                        {loc.Code} - {loc.Description}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.LocationID && (
                <span className="text-red-500 text-sm">{errors.LocationID.message}</span>
              )}
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Airline (blank = all airlines)</div>
            <div className="w-35">
              <Controller
                control={control}
                name="AirlineID"
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                  >
                    <option value="">All Airlines</option>
                    {airlines.map((air) => (
                      <option key={air.AirlineID} value={air.AirlineID}>
                        {air.AirlineLetterCode} - {air.Description}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Rate ($)</div>
            <div className="w-35">
              <Controller
                control={control}
                name="Rate"
                rules={{ required: "Required", min: { value: 0, message: "Must be positive" } }}
                render={({ field: { value, onChange } }) => (
                  <input
                    type="number"
                    step="0.01"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
              {errors.Rate && (
                <span className="text-red-500 text-sm">{errors.Rate.message}</span>
              )}
            </div>
          </div>
          <div className="flex gap-4 mt-4 save-button">
            <button
              onClick={handleSubmit(onSave)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{rate ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={onCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex">
            <h5 className="mb-0">Default Import Rates</h5>
            <button
              className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
              onClick={onAddNew}
            >
              <span>Add New</span>
            </button>
          </div>
          <table className="table-auto w-full mt-4 border border-gray-200">
            <thead>
              <tr className="bg-gray-100 cursor-pointer">
                <th className="px-4 py-2 border">Airport</th>
                <th className="px-4 py-2 border">Airline</th>
                <th className="px-4 py-2 border">Rate</th>
                <th className="px-4 py-2 border">Edit</th>
                <th className="px-4 py-2 border">Delete</th>
              </tr>
            </thead>
            <tbody>
              {ratesData.map((item) => (
                <tr key={item.ID} className="hover:bg-gray-50">
                  <td className="px-4 py-2 border">
                    {item.Locations?.Code || ""} - {item.Locations?.Description || ""}
                  </td>
                  <td className="px-4 py-2 border">
                    {item.Airlines
                      ? `${item.Airlines.AirlineLetterCode} - ${item.Airlines.Description}`
                      : "All Airlines"}
                  </td>
                  <td className="px-4 py-2 border">${parseFloat(item.Rate).toFixed(2)}</td>
                  <td className="px-4 py-2 border cursor-pointer">
                    <FaEdit
                      onClick={() => {
                        setRate(item);
                        setIsEdit(true);
                      }}
                    />
                  </td>
                  <td className="px-4 py-2 border cursor-pointer">
                    <FaTrash
                      className="text-red-500"
                      onClick={() => onDelete(item.ID)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page === 1}
                    className={`pagination-item ${page === 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * PAGE_SIZE > totalCount}
                    className={`pagination-item ${
                      page * PAGE_SIZE > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RatesNew;
